import { Component, OnInit, ViewChild, ElementRef, OnDestroy, inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { debounceTime, distinctUntilChanged, takeWhile, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SearchDto, PopularSearch } from '../../../../dto';
import { Pagination, ErrorResponse, ERROR_TYPE } from '../../../../core';
import { SearchService } from '../../../../services/search.service';
import { BaseComponent } from '../../../../core';
import { GaService, SearchResultType } from '../../../../services';

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
  standalone: false,
})
export class SearchModalComponent extends BaseComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private searchService = inject(SearchService);
  private gaService = inject(GaService);

  SEARCH_MODE: any = Object.freeze({
    RECENT: 'recent',
    TREND: 'trend',
  });

  SEARCH_TYPE = Object.freeze({
    BRAND: 'BRAND',
    PRODUCT: 'PRODUCT',
    SEARCH: 'SEARCH',
  });

  @ViewChild('Keyword', { read: ElementRef, static: false })
  Keyword: ElementRef;

  // searchTerm: string;
  isShowSearchModal = false;
  isSearching = false;
  searchMode = this.SEARCH_MODE.RECENT;
  private isErrorDialogOpen = false;
  isSearchingLists: SearchDto[];
  brandsType: SearchDto[];
  productType: SearchDto[];

  searchForm: FormGroup;
  recentSearches: Array<string> = [];
  pagination: Pagination = new Pagination();

  popularSearches: PopularSearch[];

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      keyword: ['', [Validators.required]],
    });
    this.searchForm
      .get('keyword')
      .valueChanges.pipe(takeWhile(() => this.isAlive))
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((keyword) => {
        this.isSearching = (keyword && keyword !== '') || (keyword && keyword.length > 2);
        if (keyword) {
          this.searchService.getSearch(keyword).subscribe(
            (searchDto) => {
              this.isSearchingLists = searchDto;

              this.brandsType = this.isSearchingLists.filter((list) => list.searchType.includes('BRAND'));
              this.productType = this.isSearchingLists.filter((list) => list.searchType.includes('PRODUCT'));

              this.sendKeywordToGA(keyword, this.brandsType, this.productType);
            },
            (error: ErrorResponse) => {
              if (error.type === ERROR_TYPE.VALIDATOR && error.errors && error.errors['keyword']) {
                const field = error.errors['keyword'];
                if (field.message && field.message.toLowerCase().includes('shorter than or equal to')) {
                  // 이미 에러 다이얼로그가 열려있으면 무시
                  if (this.isErrorDialogOpen) {
                    return;
                  }

                  this.isErrorDialogOpen = true;
                  const dialog = this._openDialog({
                    message: field.message || 'keyword must be shorter than or equal to 128 characters',
                    close: () => {
                      const currentValue = this.searchForm.get('keyword').value;
                      if (currentValue && currentValue.length > 100) {
                        this.searchForm.get('keyword').setValue(currentValue.substring(0, 100));
                      }
                      this.isErrorDialogOpen = false;
                      dialog.close();
                    },
                  });
                }
                return;
              }
              console.error('Search error', error);
            }
          );
        }
      });

    localStorage.getItem('search')
      ? (this.recentSearches = JSON.parse(localStorage.getItem('search')))
      : localStorage.setItem('search', JSON.stringify([]));

    this.searchService.getPopularSearch().subscribe((popularSearches) => {
      this.popularSearches = popularSearches;
    });
  }

  initModalMaximized(dialog: Dialog) {
    setTimeout(() => {
      dialog.maximize();
    }, 0);
  }

  // updateSearch(e) {
  //   this.searchTerm = e.target.value;
  // }

  openSearchModal() {
    this.isShowSearchModal = true;
    this.gaService.logSearchBarOpen();
    setTimeout(() => {
      this.Keyword.nativeElement.focus();
    }, 500);
  }

  onClearSearch() {
    this.searchForm.get('keyword').setValue(null);
    this.searchMode = this.SEARCH_MODE.RECENT;
  }

  onSearch() {
    const keyword = this.searchForm.get('keyword').value;
    const isInclude = this.recentSearches.includes(keyword);
    if (keyword) {
      this.router.navigate([`/search`], { queryParams: { q: keyword } });
      this.isShowSearchModal = false;
      if (this.recentSearches.length < 10 && !isInclude) {
        this.recentSearches.unshift(keyword);
      } else if (!isInclude) {
        this.recentSearches.pop();
        this.recentSearches.unshift(keyword);
      }

      localStorage.setItem('search', JSON.stringify(this.recentSearches));
    } else {
      const dialog = this._openDialog({
        message: this.i18n.t('DIALOG.SEARCH.REQUIRED'),
        close: () => {
          dialog.close();
        },
      });
    }
  }

  onClickKeyword(keyword: string, isTrend?: boolean) {
    this.router.navigate([`/search`], { queryParams: { q: keyword } });
    this.isShowSearchModal = false;
    if (isTrend) {
      this.gaService.logClickSearchSuggestion(keyword);
    }
  }

  onCloseSearchModal(searchType?: string, id?: string | number) {
    this.isSearchingLists = [];
    this.isShowSearchModal = false;
    this.searchForm.get('keyword').setValue(null);

    if (searchType && id) {
      switch (searchType) {
        case this.SEARCH_TYPE.BRAND:
          this.router.navigate([`/brand`, id]);
          break;
        case this.SEARCH_TYPE.PRODUCT:
          this.searchForm.get('keyword').setValue('');
          this.router.navigate([`/product`, id], {
            queryParams: { list: 'search' },
          });
          break;
      }
    }
  }

  removeItems(event: Event, i: number) {
    this.recentSearches.splice(i, 1);
    localStorage.setItem('search', JSON.stringify(this.recentSearches));
  }

  clearAllItems() {
    const dialog = this._openDialog({
      message: this.i18n.t('DIALOG.SEARCH.DELETE'),
      close: () => {
        localStorage.removeItem('search');
        this.recentSearches = [];
        dialog.close();
      },
      cancel: () => {
        dialog.close();
      },
    });
  }

  sendKeywordToGA(keyword: string, brandsType: SearchDto[], productType: SearchDto[]) {
    const hasBrand = brandsType.length > 0;
    const hasProduct = productType.length > 0;
    let resultType: SearchResultType;
    if (!hasBrand && !hasProduct) {
      resultType = 'brand-product-not-found';
    } else if (hasBrand && !hasProduct) {
      resultType = 'brand-only-found';
    } else if (!hasBrand && hasProduct) {
      resultType = 'product-only-found';
    } else {
      resultType = 'brand-product-found';
    }

    this.gaService.logSearch(keyword, resultType, brandsType.length, productType.length);
  }
}
